/**
 * La regola dello username, **replicata** dal backend
 * (`src/domains/profile/username/rules.ts` in sevedemo-express, SPEC
 * custom-profile-link AC5/AC6).
 *
 * Qui serve a dare un riscontro mentre si scrive — il campo dice «carattere
 * non ammesso» prima di partire con una richiesta — ma l'autorità resta il
 * backend: ciò che passasse di qui e non di là torna 400 `USERNAME_INVALID`.
 * Le due copie vanno cambiate insieme; a divergere, vince il BE.
 *
 * Modulo puro: nessun `t()` e nessuna dipendenza da Solid, così la regola è
 * verificabile in Node e riusabile dallo scrub degli eventi (T14).
 */
export const USERNAME_MIN = 3;
export const USERNAME_MAX = 40;

export const USERNAME_PATTERN = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])?$/;

export type UsernameProblem = "short" | "long" | "chars" | "edge";

const USERNAME_PROBLEMS: readonly string[] = [
  "short",
  "long",
  "chars",
  "edge",
] satisfies readonly UsernameProblem[];

/**
 * Restringe il campo `problem` del 400 del backend, che arriva come `string`
 * grezza dalla rete: senza la guardia il messaggio da mostrare sarebbe
 * `unknown` per `t()`.
 */
export const isUsernameProblem = (value: unknown): value is UsernameProblem =>
  typeof value === "string" && USERNAME_PROBLEMS.includes(value);

/**
 * Normalizzazione **mentre si scrive** (AC11): maiuscole in minuscole, spazi
 * in trattini. Nient'altro viene tolto: un `_` o un accento restano nel campo
 * e producono il messaggio della regola violata, invece di sparire sotto le
 * dita di chi scrive senza spiegazione.
 */
export const normalizeUsernameInput = (raw: string): string =>
  raw.toLowerCase().replace(/\s/g, "-");

/** `null` quando il valore rispetta la regola; altrimenti il primo problema. */
export const usernameProblem = (value: string): UsernameProblem | null => {
  if (value.length < USERNAME_MIN) {
    return "short";
  }
  if (value.length > USERNAME_MAX) {
    return "long";
  }
  if (!/^[a-z0-9-]+$/.test(value)) {
    return "chars";
  }
  if (value.startsWith("-") || value.endsWith("-")) {
    return "edge";
  }
  return null;
};

/**
 * Lo username **ereditato** dalla generazione automatica di prima della spec
 * (`giuliaferrante_8f3a1`, `Alena.Bosco_12`): non rispetta la regola di oggi,
 * ma è un link vivo e non è un errore dell'utente. La card lo mostra con una
 * copy neutra invece della regola nuda (D16, AC10).
 */
export const isLegacyUsername = (value: string | null): boolean =>
  value !== null && usernameProblem(value) !== null;

/**
 * Gli stessi valori di `RESERVED_USERNAMES` nel backend (D4): le rotte reali
 * sotto `/profile/` più brand e ruoli. Qui la lista serve allo scrub degli
 * eventi (T14), che deve distinguere `/profile/toolkit` — una pagina
 * dell'app — da `/profile/giulia-design` — un profilo pubblico.
 */
export const RESERVED_PROFILE_SEGMENTS = [
  "me",
  "applications",
  "proposals",
  "partners",
  "toolkit",
  "company",
  "sevedemo",
  "admin",
  "support",
  "api",
  "login",
  "signup",
  "help",
  "team",
] as const;

export const PROFILE_LINK_ERROR_KEYS = {
  short: "profile.link.error.short",
  long: "profile.link.error.long",
  chars: "profile.link.error.chars",
  edge: "profile.link.error.edge",
} as const satisfies Record<UsernameProblem, string>;
